import React, { useState, useEffect } from "react";
import { Box, Typography, Tabs, Tab } from "@mui/material";
import { format, isToday, isYesterday, parseISO } from "date-fns";
import Chat from "./Chat";
import Notes from "./Notes";
import MixedActivityView from "./MixedActivityView";
import TermsAndConditions from "./TermsAndConditions";

// Helper function to format date for display
const formatEventDate = (dateString) => {
  const date = parseISO(dateString);

  if (isToday(date)) {
    return "TODAY";
  } else if (isYesterday(date)) {
    return "YESTERDAY";
  } else {
    return format(date, "MM/dd/yyyy");
  }
};

// Helper function to format time for display
const formatEventTime = (dateString) => {
  const date = parseISO(dateString);
  return format(date, "HH:mm");
};

// Group events by date
const groupEventsByDate = (events) => {
  const grouped = {};

  events?.forEach((event) => {
    const dateKey = formatEventDate(event.createdAt);
    if (!grouped[dateKey]) {
      grouped[dateKey] = [];
    }
    grouped[dateKey].push(event);
  });

  // Sort dates in descending order (newest first)
  return Object.keys(grouped)
    .sort((a, b) => {
      // Ensure "TODAY" comes first, then "YESTERDAY", then other dates in descending order
      if (a === "TODAY") return -1;
      if (b === "TODAY") return 1;
      if (a === "YESTERDAY") return -1;
      if (b === "YESTERDAY") return 1;

      // For other dates, compare as dates
      const dateA = a.split("/").reverse().join("-");
      const dateB = b.split("/").reverse().join("-");
      return dateB.localeCompare(dateA);
    })
    .reduce((result, key) => {
      result[key] = grouped[key];
      return result;
    }, {});
};

const EventsTimeline = ({ order, isManifest = false, manifestUuid }) => {
  // Get manifestUuid from order if not provided as prop
  const finalManifestUuid = manifestUuid || order?.uuid || order?.manifestUuid;

  // Get status from order/manifestData
  const manifestStatus = order?.status || "";
  const statusUpper = manifestStatus?.toUpperCase() || "";

  // Check if Chat tab should be enabled (hide Chat tab when status is PLN or COM, or when not on manifest)
  const isChatEnabled =
    isManifest && statusUpper !== "PLN" && statusUpper !== "COM";

  // Calculate tab indices based on whether Chat is shown
  const getTabIndex = (tabName) => {
    if (tabName === "all") return 0;
    if (tabName === "chat") return isManifest ? 1 : -1; // -1 means not shown
    if (tabName === "events") return isManifest ? 2 : 1;
    if (tabName === "notes") return isManifest ? 3 : 2;
    if (tabName === "terms") return isManifest ? -1 : 3; // -1 means not shown in manifest
    return 0;
  };

  // Default to All tab (index 0)
  const [activeTab, setActiveTab] = useState(0);

  // Group events by date
  const groupedEvents = groupEventsByDate(order?.events);

  // Handle Chat tab enable/disable based on status changes
  useEffect(() => {
    // If Chat tab becomes disabled and user is on Chat tab, switch to All
    if (!isChatEnabled && activeTab === getTabIndex("chat")) {
      setActiveTab(0);
    }
  }, [isChatEnabled, activeTab]);
  // alert(JSON.stringify(order, null, 2));
  return (
    <Box
      elevation={0}
      sx={{
        borderRadius: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "white",
        borderLeft: "1px solid #e9ecef",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          backgroundColor: "white",
          borderBottom: "1px solid #e9ecef",
          boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
          borderRadius: 0,
        }}
      >
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #f0f0f0",
            borderRadius: 0,
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontWeight: 600,
              fontSize: { xs: "1rem", sm: "1.25rem" },
              color: "text.primary",
            }}
          >
            Activity
          </Typography>
        </Box>

        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            borderRadius: 0,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            aria-label="activity tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: "48px",
              "& .MuiTabs-indicator": {
                backgroundColor: "#1e55bf",
                height: 3,
                borderRadius: "0",
              },
              "& .MuiTabs-scrollButtons": {
                width: "32px",
                "&.Mui-disabled": {
                  opacity: 0.3,
                },
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 500,
                paddingLeft: { xs: "12px", sm: "16px" },
                paddingRight: { xs: "12px", sm: "20px" },
                minWidth: { xs: "60px", sm: "80px" },
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                transition: "all 0.2s ease",
                "&.Mui-selected": {
                  color: "#1e55bf",
                  fontWeight: 600,
                },
                "&:hover": {
                  backgroundColor: "rgba(30, 85, 191, 0.04)",
                },
                "&.Mui-disabled": {
                  opacity: 0.4,
                  cursor: "not-allowed",
                },
              },
            }}
          >
            <Tab label="All" />
            {isManifest && (
              <Tab
                label="Chat"
                disabled={!isChatEnabled}
                sx={{
                  "&.Mui-disabled": {
                    opacity: 0.4,
                    cursor: "not-allowed",
                  },
                }}
              />
            )}
            <Tab label="Events" />
            <Tab label="Notes" />
            {!isManifest && <Tab label="Terms and Conditions" />}
          </Tabs>
        </Box>
      </Box>

      {/* Content Area */}
      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          borderRadius: 0,
        }}
      >
        {/* All Tab - Shows everything mixed */}
        {activeTab === 0 && (
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              borderRadius: 0,
            }}
          >
            <MixedActivityView
              order={order}
              manifestUuid={finalManifestUuid}
              isManifest={isManifest}
            />
          </Box>
        )}

        {/* Chat Tab */}
        {activeTab === getTabIndex("chat") && (
          <Box sx={{ flex: 1, overflow: "hidden", p: { xs: 0, sm: 0 } }}>
            <Chat manifestUuid={finalManifestUuid} />
          </Box>
        )}

        {/* Events Tab */}
        {activeTab === getTabIndex("events") && (
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              overflowY: "auto",
              flex: 1,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#c1c1c1",
                borderRadius: "3px",
                "&:hover": {
                  background: "#a8a8a8",
                },
              },
            }}
          >
            <>
              {order?.billingParty?.notes && (
                <Box
                  sx={{
                    backgroundColor: "#FAF9EF",
                    border: "1px solid #f0e68c",
                    borderRadius: 2,
                    p: { xs: 1.5, sm: 2 },
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#8b7d3a",
                      fontWeight: 600,
                      mb: 1,
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  >
                    Notes
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#b5b3a1",
                      fontSize: { xs: "0.8rem", sm: "0.875rem" },
                      lineHeight: 1.6,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {order.billingParty.notes}
                  </Typography>
                </Box>
              )}
              {Object.keys(groupedEvents).length > 0 ? (
                Object.entries(groupedEvents).map(([date, dateEvents]) => (
                  <Box key={date} sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        textAlign: "center",
                        mb: 1,
                        position: "sticky",
                        top: -20,
                        zIndex: 1,
                        backgroundColor: "white",
                        py: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: "#1e55bf",
                          backgroundColor: "#e3f2fd",
                          borderRadius: 1,
                          px: 2,
                          py: 0.75,
                          display: "inline-block",
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          border: "1px solid #bbdefb",
                        }}
                      >
                        {date}
                      </Typography>
                    </Box>

                    {dateEvents.map((event, index) => {
                      const fullName = event.user
                        ? `${event.user.firstName} ${event.user.lastName}`
                        : "System";

                      return (
                        <Box
                          key={event.uuid}
                          sx={{
                            display: "flex",
                            mb: 2.5,
                            alignItems: "flex-start",
                            gap: 1.5,
                            p: { xs: 1, sm: 1.5 },
                            borderRadius: 2,
                            transition: "background-color 0.2s",
                            "&:hover": {
                              backgroundColor: "#f8f9fa",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: "4px",
                              height: "4px",
                              borderRadius: "50%",
                              backgroundColor: "#1e55bf",
                              mt: 1,
                              flexShrink: 0,
                            }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                                lineHeight: 1.6,
                                mb: 0.5,
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  fontWeight: 600,
                                  color: "text.primary",
                                  mr: 1,
                                }}
                              >
                                {fullName}
                              </Box>
                              <Box
                                component="span"
                                sx={{
                                  color: "text.secondary",
                                }}
                              >
                                {event.text.substring(fullName.length + 1)}
                              </Box>
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                fontSize: { xs: "0.65rem", sm: "0.7rem" },
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Box component="span">
                                {formatEventTime(event.createdAt)}
                              </Box>
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                ))
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 8,
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: "center",
                      color: "text.secondary",
                      fontWeight: 500,
                    }}
                  >
                    No events to display
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      textAlign: "center",
                      color: "text.secondary",
                    }}
                  >
                    Activity events will appear here
                  </Typography>
                </Box>
              )}
            </>
          </Box>
        )}

        {/* Notes Tab */}
        {activeTab === getTabIndex("notes") && (
          <Box sx={{ flex: 1, overflow: "hidden", p: { xs: 0, sm: 0 } }}>
            <Notes
              shipperNotes={null}
              consigneeNotes={null}
              orderId={order?.uuid || order?.id}
              manifestId={finalManifestUuid}
              isManifest={isManifest}
            />
          </Box>
        )}

        {/* Terms and Conditions Tab */}
        {activeTab === getTabIndex("terms") && (
          <Box sx={{ flex: 1, overflow: "hidden", p: { xs: 0, sm: 0 } }}>
            <TermsAndConditions billingPartyUuid={order?.billingParty?.uuid} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EventsTimeline;
